import { Accordion, AccordionItem, Skeleton, VStack } from '@chakra-ui/react';
import React, { useMemo } from 'react';

import { Typography } from 'packages/common-frontend/components/Typography';
import { useGetInvoicesByLoanIdQuery } from 'packages/invoices/requests/invoices.gql.generated';
import { InvoiceSnapshot } from 'packages/invoices/ui/InvoiceSnapshotContainer';
import {
  FundingRequestEntitiesFragmentGQLType,
  ResolvedLoanFragmentGQLType,
} from 'packages/loans/requests/loans.gql.generated';
import { FundingRequestSnapshotContainer } from 'packages/loans/ui/FundingRequestContainer';

export const FundingRequestsAndInvoicesSnapshotContainer = ({
  loan,
  fundingRequests,
  isLoading,
}: {
  loan: ResolvedLoanFragmentGQLType;
  fundingRequests: FundingRequestEntitiesFragmentGQLType[];
  isLoading: boolean;
}) => {
  const {
    data: invoicesData,
    loading: isInvoicesLoading,
    error: invoicesError,
  } = useGetInvoicesByLoanIdQuery({
    variables: { loanId: loan.id },
  });
  if (invoicesError) throw invoicesError;

  const sortedFundingRequestsAndInvoices = useMemo(() => {
    const combined = [
      ...(fundingRequests?.map((fundingRequest) => ({
        type: 'fundingRequest' as const,
        item: fundingRequest,
        date: fundingRequest.originationDate,
        // default open if funding request is not yet approved or there are funding components that are not marked as funded
        defaultOpen:
          fundingRequest.fundingRequestStatus === 'submitted' ||
          fundingRequest.fundingComponents?.some(
            (component) => !component.fundTransferDate,
          ),
        createdAt: fundingRequest.fundingRequestCreatedAt,
      })) ?? []),
      ...(invoicesData?.invoices?.map((invoice) => ({
        type: 'invoice' as const,
        item: invoice,
        date: invoice.dueDate,
        defaultOpen: invoice.status === 'created',
        createdAt: invoice.created_at,
      })) ?? []),
    ];

    return combined.sort((a, b) => {
      if (b.date !== a.date) {
        return b.date.localeCompare(a.date);
      }
      return b.createdAt - a.createdAt;
    });
  }, [fundingRequests, invoicesData]);

  const defaultOpenIndices: number[] = useMemo(() => {
    const openIndices: number[] = [];
    sortedFundingRequestsAndInvoices.forEach((item, index) => {
      if (item.defaultOpen) {
        openIndices.push(index);
      }
    });
    return openIndices;
  }, [sortedFundingRequestsAndInvoices]);

  if (isLoading || isInvoicesLoading) return <Skeleton h="200px" />;

  return (
    <VStack w="100%" align="start" gap="1rem">
      <Typography variant="section-title" trim>
        Past Draws & Payments
      </Typography>
      <Accordion
        allowMultiple
        w="100%"
        border="none"
        defaultIndex={defaultOpenIndices}
      >
        {sortedFundingRequestsAndInvoices.map((item) =>
          item.type === 'fundingRequest' ? (
            <AccordionItem
              key={item.item.fundingRequestId}
              border="none"
              mt="1rem"
            >
              <FundingRequestSnapshotContainer
                loan={loan}
                fundingRequest={item.item}
                lenderShares={loan.funding.lenderShares}
                loading={isLoading}
              />
            </AccordionItem>
          ) : (
            <AccordionItem key={item.item.id} border="none" mt="1rem">
              <InvoiceSnapshot invoice={item.item} />
            </AccordionItem>
          ),
        )}
      </Accordion>
    </VStack>
  );
};
